import { Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { UsagePopover } from '@/components/common/UsageInfo'
import type {
  WarehouseLookupEntry,
  WarehouseLookupEntryUsage,
} from '@/types/adminWarehouseLookups'

interface WarehouseLookupEntriesGridProps {
  entries: WarehouseLookupEntry[]
  isLoading: boolean
  usageMap: Record<
    string,
    { data?: WarehouseLookupEntryUsage; isLoading: boolean }
  >
  onEdit: (entry: WarehouseLookupEntry) => void
  onDelete: (entry: WarehouseLookupEntry) => void
  onFetchUsage: (entry: WarehouseLookupEntry) => void
}

export default function WarehouseLookupEntriesGrid({
  entries,
  isLoading,
  usageMap,
  onEdit,
  onDelete,
  onFetchUsage,
}: WarehouseLookupEntriesGridProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading lookup entries...
      </div>
    )
  }

  if (!entries.length) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
        No entries found for the selected table and filters.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Key</TableHead>
            <TableHead>Text Value</TableHead>
            <TableHead>Numeric Value</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(entry => {
            const usage = usageMap[entry.lookup_key]

            return (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">
                  {entry.lookup_key}
                </TableCell>
                <TableCell>{entry.value_text || '-'}</TableCell>
                <TableCell>
                  {typeof entry.value_num === 'number' ? entry.value_num : '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={entry.is_active ? 'success' : 'neutral'}>
                    {entry.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <UsagePopover
                      usage={usage?.data}
                      isLoading={usage?.isLoading ?? false}
                      label={entry.lookup_key}
                      onOpen={() => onFetchUsage(entry)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => onEdit(entry)}
                      aria-label={`Edit ${entry.lookup_key}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => onDelete(entry)}
                      disabled={!entry.is_active}
                      aria-label={`Deactivate ${entry.lookup_key}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
